// The Image features shading's features kept in this browser (IndexedDB, which stores typed arrays as they are), so a
// photo is looked at once, not on every open. Only what the shading and the photo overlay draw is kept (about 6 kB a
// photo). Keyed by the camera images' manifest (hashed: a new export of the photos is new keys), the frame, and the
// detector's version and options. Best effort, like packstore.ts: without it (private mode, quota) the photos are
// looked at again.
import type { FrameFeats } from './feats.ts';
import type { Segs } from './segments.ts';

/** Bump when the detectors (corners.ts, segments.ts, depthlines.ts) change what they find. */
export const FEAT_STORE_V = 1;
const DB = 'cloudview-photo', STORE = 'feats';
let opened: Promise<IDBDatabase | null> | null = null;

function db(): Promise<IDBDatabase | null> {
  opened ??= new Promise(res => {
    try {
      const r = indexedDB.open(DB, 1);
      r.onupgradeneeded = () => { r.result.createObjectStore(STORE); };
      r.onsuccess = () => res(r.result);
      r.onerror = () => res(null);
      r.onblocked = () => res(null);
    } catch { res(null); }
  });
  return opened;
}

/** What is kept of a frame's features. */
export interface Kept { xy: Float32Array; seg: Float32Array; jump: Float32Array; crease: Float32Array }

export const keep = (ff: FrameFeats): Kept => ({
  xy: ff.corners?.xy.slice(0, 2 * ff.corners.n) ?? new Float32Array(0), seg: ff.segs?.seg.slice(0, 4 * ff.segs.n) ?? new Float32Array(0),
  jump: ff.depth.jump.seg.slice(0, 4 * ff.depth.jump.n), crease: ff.depth.crease.seg.slice(0, 4 * ff.depth.crease.n),
});

const segsOf = (seg: Float32Array): Segs => {
  const n = seg.length >> 2;
  return { n, seg, contrast: new Float32Array(n), side: new Float32Array(8 * n), lnfa: new Float32Array(n) };
};

/** A FrameFeats again from what was kept (what it didn't keep: empty). */
export function unkeep(key: string, k: Kept): FrameFeats {
  const n = k.xy.length >> 1;
  return {
    key, corners: { n, xy: k.xy, score: new Float32Array(n), desc: new Uint8Array(0) }, segs: segsOf(k.seg),
    depth: { jump: segsOf(k.jump), crease: segsOf(k.crease) }, bends: { off: Uint32Array.of(0), pts: new Float32Array(0) }, z: new Uint16Array(0), masked: false, ms: 0,
  };
}

/** The store's key of a frame: its scan's camera images (their manifest) and the detector's options. */
export const featKey = (images: string, f: number, opts: object) => `${FEAT_STORE_V}|${images}|${JSON.stringify(opts)}|${f}`;

/** The kept features of keys (null where none), in one read. */
export async function getFeats(keys: string[]): Promise<(Kept | null)[]> {
  const d = await db();
  if (!d || !keys.length) return keys.map(() => null);
  return new Promise(res => {
    try {
      const s = d.transaction(STORE, 'readonly').objectStore(STORE), out: (Kept | null)[] = keys.map(() => null);
      let left = keys.length;
      keys.forEach((k, i) => {
        const r = s.get(k);
        r.onsuccess = () => { out[i] = (r.result as Kept | undefined) ?? null; if (!--left) res(out); };
        r.onerror = () => { if (!--left) res(out); };
      });
    } catch { res(keys.map(() => null)); }
  });
}

/** Keep these (one write). */
export async function putFeats(items: [string, Kept][]): Promise<void> {
  const d = await db();
  if (!d || !items.length) return;
  await new Promise<void>(res => {
    try {
      const t = d.transaction(STORE, 'readwrite'), s = t.objectStore(STORE);
      for (const [k, v] of items) s.put(v, k);
      t.oncomplete = t.onerror = t.onabort = () => res();
    } catch { res(); }
  });
}

/** Forget every kept frame (the settings' Clear browser cache). */
export async function clearFeats(): Promise<void> {
  const d = await opened?.catch(() => null);
  d?.close();
  opened = null;
  await new Promise<void>(res => { try { const r = indexedDB.deleteDatabase(DB); r.onsuccess = r.onerror = r.onblocked = () => res(); } catch { res(); } });
}
